import { contrast, deriveTokens, mix, modeForGround, parseHex, type Mode, type ThemeTokens } from "../portal/palette";
import { fromHsv, groundFor, hsvOf, type BrandColours } from "./logoColours";

/**
 * Schemes offered for one logo, each a 60-30-10 balance of ground, ink and
 * accent (design canvas, owner-reviewed 2026-09-12).
 *
 * The first three are always Classic, Inverted and Soft. `variationsFor` goes
 * on from there -- other page grounds, accent treatments and the logo's other
 * colours -- so an operator can keep asking for more from the same logo.
 *
 * 🔑 THE PREVIEW USES THE PORTAL'S OWN FUNCTION. Tokens come from
 * src/portal/palette.ts -- the code the guest's page runs -- so what the
 * operator is shown here is what the portal will paint, not a lookalike.
 * `mode` follows radhotspotapi's rule; the server derives it again on save and
 * never accepts it from the console.
 */

export interface Scheme {
  /** Unique within one logo's list; also the default theme name (THEME_NAME-valid). */
  id: string;
  label: string;
  note: string;
  ground: string;
  ink: string;
  accent: string;
  mode: Mode;
  tokens: ThemeTokens;
  /** Measured, never asserted. */
  ratios: { text: number; button: number; link: number; rawAccent: number };
  /** The accent failed as text on the surface, so the link is a darkened (or lightened) accent. */
  linkAdjusted: boolean;
}

/** Most variations one logo offers, the first three included. */
export const MAX_VARIATIONS = 30;
/** A button fill closer than this to its page is not an accent. */
const MIN_FILL_CONTRAST = 1.4;
/** Two schemes whose painted colours all sit within this (per sRGB channel) look the same. */
const SAME_WITHIN = 14;

function build(id: string, label: string, note: string, ground: string, ink: string, accent: string): Scheme {
  const mode = modeForGround(ground);
  const tokens = deriveTokens(ground, ink, accent, mode)!;
  return {
    id, label, note, ground, ink, accent, mode, tokens,
    ratios: {
      text: contrast(tokens["--text"], tokens["--surface"]),
      button: contrast(tokens["--on-accent"], accent),
      link: contrast(tokens["--link"], tokens["--surface"]),
      rawAccent: contrast(accent, tokens["--surface"]),
    },
    linkAdjusted: tokens["--link"] !== accent,
  };
}

/** A dark ground: `ink` darkened until `light` reads on it at 7:1. */
function darkGround(ink: string, light: string): string {
  for (let t = 0.3; t <= 0.95; t += 0.05) {
    const g = mix(ink, "#000000", t);
    if (modeForGround(g) === "dark" && contrast(g, light) >= 7) return g;
  }
  return mix(ink, "#000000", 0.95);
}

/** Colours an operator chose by hand, measured like every offered scheme. Each must be #RRGGBB. */
export function customScheme(ground: string, ink: string, accent: string): Scheme {
  return build("Custom", "Custom", "Your own colours.", ground.toUpperCase(), ink.toUpperCase(), accent.toUpperCase());
}

export function schemesFor(b: BrandColours): Scheme[] {
  return [
    build("Classic", "Classic", "White page, your ink for text, your accent on buttons.", "#FFFFFF", b.ink, b.accent),
    build("Inverted", "Inverted", "A dark page in your ink colour, light text, the accent stands out.", darkGround(b.ink, b.ground), b.ground, b.accent),
    build("Soft", "Soft", "A tinted page and a quieter accent.", b.ground, b.ink, mix(b.accent, b.ink, 0.14)),
  ];
}

interface Pair { key: string; words: string; ink: string; accent: string }
interface Treatment { key: string; words: string; accent: (p: Pair) => string }
interface Ground { key: string; words: string; paint: (p: Pair, accent: string) => { ground: string; ink: string } }

const TREATMENTS: Treatment[] = [
  { key: "", words: "your accent", accent: (p) => p.accent },
  { key: "Quiet", words: "a quieter accent", accent: (p) => mix(p.accent, p.ink, 0.14) },
  {
    key: "Bright", words: "a brighter accent",
    accent: (p) => { const c = hsvOf(p.accent); return fromHsv(c.h, c.s + 0.15, c.v + 0.12); },
  },
  {
    key: "Near", words: "a neighbouring hue",
    accent: (p) => { const c = hsvOf(p.accent); return fromHsv(c.h + 30, c.s, c.v); },
  },
  {
    key: "Opposite", words: "a hue opposite your ink",
    accent: (p) => { const c = hsvOf(p.ink); return fromHsv(c.h + 165, Math.min(0.9, Math.max(0.55, c.s)), 0.85); },
  },
];

const GROUNDS: Ground[] = [
  { key: "White", words: "White page", paint: (p) => ({ ground: "#FFFFFF", ink: p.ink }) },
  { key: "Tinted", words: "Tinted page", paint: (p) => ({ ground: groundFor(p.ink), ink: p.ink }) },
  {
    key: "Dark", words: "Dark page",
    paint: (p) => { const light = groundFor(p.ink); return { ground: darkGround(p.ink, light), ink: light }; },
  },
  { key: "Warm", words: "Page tinted to the accent", paint: (p, accent) => ({ ground: groundFor(accent), ink: p.ink }) },
  {
    key: "Deep", words: "Deeper tinted page",
    paint: (p) => { const c = hsvOf(p.ink); return { ground: c.s < 0.1 ? "#EBEBEB" : fromHsv(c.h, 0.07, 0.95), ink: p.ink }; },
  },
];

function pairsFor(b: BrandColours): Pair[] {
  const pairs: Pair[] = [{ key: "", words: "", ink: b.ink, accent: b.accent }];
  const others = b.colours.filter((c) => c !== b.ink && c !== b.accent);
  others.forEach((c, i) => {
    pairs.push({ key: `Alt${i + 1}`, words: `accent ${c} from your logo`, ink: b.ink, accent: c });
  });
  // The accent writes and the ink fills: only when both came from the logo.
  if (!b.accentDerived) pairs.push({ key: "Swap", words: "ink and accent swapped", ink: b.accent, accent: b.ink });
  return pairs;
}

const sameColour = (a: string, b: string) => {
  const x = parseHex(a)!, y = parseHex(b)!;
  return [0, 1, 2].every((i) => Math.abs(x[i]! - y[i]!) <= SAME_WITHIN);
};

const PAINTED = ["--bg", "--surface", "--text", "--link", "--stage"] as const;

function looksLike(a: Scheme, b: Scheme): boolean {
  return sameColour(a.accent, b.accent) && PAINTED.every((t) => sameColour(a.tokens[t], b.tokens[t]));
}

/**
 * Classic, Inverted and Soft, then further readable, visibly different schemes
 * for the same logo, in a stable order: the simplest changes come first (one
 * of page, accent treatment or colour pair differs), combinations later.
 */
export function variationsFor(b: BrandColours, limit = MAX_VARIATIONS): Scheme[] {
  const out = schemesFor(b);
  const pairs = pairsFor(b);
  const candidates: Array<{ rank: number; scheme: Scheme }> = [];

  pairs.forEach((p, pi) => {
    TREATMENTS.forEach((t, ti) => {
      // A derived accent is already "opposite the ink"; don't offer it twice.
      if (t.key === "Opposite" && b.accentDerived && pi === 0) return;
      const accent = t.accent(p);
      GROUNDS.forEach((g, gi) => {
        const { ground, ink } = g.paint(p, accent);
        const id = [g.key, p.key, t.key].filter(Boolean).join("-");
        const note = [g.words, t.words, p.words].filter(Boolean).join(", ") + ".";
        const label = [g.key, p.key && (p.key === "Swap" ? "swapped" : `alt ${p.key.slice(3)}`), t.key.toLowerCase()]
          .filter(Boolean)
          .join(" · ");
        candidates.push({ rank: (pi + ti + gi) * 100 + gi * 10 + ti, scheme: build(id, label, note, ground, ink, accent) });
      });
    });
  });

  candidates.sort((x, y) => x.rank - y.rank);
  for (const { scheme } of candidates) {
    if (out.length >= limit) break;
    if (scheme.ratios.text < 4.5 || scheme.ratios.link < 4.5 || scheme.ratios.button < 4.5) continue;
    if (scheme.ratios.rawAccent < MIN_FILL_CONTRAST) continue;
    if (out.some((s) => s.id === scheme.id || looksLike(s, scheme))) continue;
    out.push(scheme);
  }
  return out;
}
